import React, { useState, createContext, lazy, Suspense } from "react";
import { Switch, Route, Redirect, useLocation } from 'wouter';
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { DarkModeProvider, useDarkMode } from "./context/DarkModeContext";
import Sidebar from "./components/Sidebar";
import { PAGE_ROLES } from './config/accessControl';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Items = lazy(() => import('./pages/Items'));
const Balance = lazy(() => import('./pages/Balance'));
const Departments = lazy(() => import('./pages/Departments'));
const StockIn = lazy(() => import('./pages/StockIn'));
const Requests = lazy(() => import('./pages/Requests'));
const StockOut = lazy(() => import('./pages/StockOut'));
const Users = lazy(() => import('./pages/UserDirectory'));
const Settings = lazy(() => import('./pages/Settings'));
const Reports = lazy(() => import('./pages/Reports'));
const ItemRequests = lazy(() => import('./pages/ItemRequests'));
const Monitoring = lazy(() => import('./pages/Monitoring'));
const Assets = lazy(() => import('./pages/Assets'));
const AssetWriteoffs = lazy(() => import('./pages/AssetWriteoffs'));
const RetiredAssets = lazy(() => import('./pages/RetiredAssets'));
const AssetScan = lazy(() => import('./pages/AssetScan'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const MyProfile = lazy(() => import('./pages/MyProfile'));

export const SidebarContext = createContext(null);

const Spinner = () => (
  <div className="app-loading">
    <img src="/TKTI logo.png" alt="TKTI" />
    <div className="app-loading__bar"><span /></div>
    <p>Tizim yuklanmoqda</p>
  </div>
);

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) {
    if (window.location.pathname !== '/login') sessionStorage.setItem('returnAfterLogin', `${window.location.pathname}${window.location.search}`);
    return <Redirect to="/login" replace />;
  }
  if (user.must_change_password && window.location.pathname !== '/change-password') return <Redirect to={'/change-password'} replace />;
  if (roles && !roles.includes(user.role)) return <Redirect to={user.role === 'tuzilma_operatori' ? '/departments' : '/dashboard'} replace />;
  return children;
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isDark } = useDarkMode();
  const [location] = useLocation();
  const isDashboard = location === '/dashboard';

  return (
    <SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className={`app-layout${isDark ? ' app-layout--dark' : ''}`}>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        )}
        <Sidebar />
        <main className={`app-main ${isDashboard ? 'app-main--dashboard' : 'app-main--compact'}`}>{children}</main>
      </div>
    </SidebarContext.Provider>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();
  const protectedRoutes = [
    { path: '/item-requests', Component: ItemRequests, roles: PAGE_ROLES.itemRequests },
    { path: '/dashboard', Component: Dashboard, roles: PAGE_ROLES.dashboard },
    { path: '/items', Component: Items, roles: PAGE_ROLES.items },
    { path: '/balance', Component: Balance, roles: PAGE_ROLES.balance },
    { path: '/stock-in', Component: StockIn, roles: PAGE_ROLES.stockIn },
    { path: '/stock-out', Component: StockOut, roles: PAGE_ROLES.stockOut },
    { path: '/requests', Component: Requests, roles: PAGE_ROLES.requests },
    { path: '/reports', Component: Reports, roles: PAGE_ROLES.reports },
    { path: '/monitoring', Component: Monitoring, roles: PAGE_ROLES.monitoring },
    { path: '/assets', Component: Assets, roles: PAGE_ROLES.assets },
    { path: '/asset-writeoffs', Component: AssetWriteoffs, roles: PAGE_ROLES.assetWriteoffs },
    { path: '/retired-assets', Component: RetiredAssets, roles: PAGE_ROLES.retiredAssets },
    { path: '/users', Component: Users, roles: PAGE_ROLES.users },
    { path: '/departments', Component: Departments, roles: PAGE_ROLES.departments },
    { path: '/settings', Component: Settings, roles: PAGE_ROLES.settings },
    { path: '/profile', Component: MyProfile, roles: PAGE_ROLES.profile },
    { path: '/asset-scan/:assetTag', Component: AssetScan, roles: PAGE_ROLES.assetScan },
  ];

  return (
    <Suspense fallback={<Spinner />}>
      <Switch>
        <Route path="/login">{user ? <Redirect to={user.must_change_password ? '/change-password' : user.role === 'tuzilma_operatori' ? '/departments' : '/dashboard'} replace /> : <Login />}</Route>
        <Route path="/change-password">{!user ? <Redirect to="/login" replace /> : !user.must_change_password ? <Redirect to={user.role === 'tuzilma_operatori' ? '/departments' : '/dashboard'} replace /> : <ChangePassword />}</Route>
        {protectedRoutes.map(({ path, Component, roles }) => (
          <Route path={path} key={path}>
            <PrivateRoute roles={roles}>
              <Layout><Component /></Layout>
            </PrivateRoute>
          </Route>
        ))}
        <Route><Redirect to={user?.must_change_password ? '/change-password' : user?.role === 'tuzilma_operatori' ? '/departments' : '/dashboard'} replace /></Route>
      </Switch>
    </Suspense>
  );
};

function App() {
  return (
    <DarkModeProvider>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
      </AuthProvider>
    </DarkModeProvider>
  );
}

export default App;
